// Bilingual content for the Angelo Zaia site (IT / EN)
import {
  Ruler,
  Network,
  ShieldAlert,
  GanttChartSquare,
  Microscope,
  ClipboardCheck,
  GraduationCap,
  Cpu,
  Boxes,
  Landmark,
} from "lucide-react";

export const HERO_IMG =
  "https://images.pexels.com/photos/3137050/pexels-photo-3137050.jpeg";
export const ABOUT_IMG =
  "https://customer-assets.emergentagent.com/job_minimal-pro-hub/artifacts/xl526p4c_DISI_L_%20Ing_Informatica_Comunicazioni_Elettronica.jpg";
export const METHOD_IMG =
  "https://images.pexels.com/photos/4252519/pexels-photo-4252519.jpeg";
export const SERVICES_IMG =
  "https://customer-assets.emergentagent.com/job_minimal-pro-hub/artifacts/c5lq14uk_coding1-e1537175323752.jpg";

export const serviceIcons = [
  Ruler,
  Network,
  ShieldAlert,
  GanttChartSquare,
  Microscope,
  ClipboardCheck,
  GraduationCap,
  Cpu,
  Boxes,
  Landmark,
];

export const content = {
  it: {
    nav: {
      about: "Studio",
      mission: "Missione",
      method: "Metodo",
      services: "Servizi",
      why: "Perché Noi",
      contact: "Contatti",
      cta: "Parliamone",
    },
    hero: {
      overline: "Ingegneria dei Sistemi & della Resilienza",
      title: "Governare il Caos con Metodo.",
      subtitle:
        "Progettiamo sistemi robusti e resilienti per un mondo complesso. Tecnologie avanzate, rigore ingegneristico e visione strategica per anticipare, adattarsi e resistere alle sfide del futuro.",
      ctaPrimary: "Inizia un progetto",
      ctaSecondary: "Scopri lo studio",
      stats: [
        { value: "25+", label: "Anni di esperienza" },
        { value: "6", label: "Settori industriali" },
        { value: "∞", label: "Sistemi resilienti" },
      ],
    },
    about: {
      overline: "Lo Studio",
      title: "Meglio con Meno.",
      paragraphs: [
        "Esploriamo l'ingegneria dei sistemi per progettare soluzioni robuste e resilienti in un mondo sempre più complesso e interconnesso. Attraverso l'integrazione di tecnologie avanzate, miglioriamo l'affidabilità dei processi, riduciamo i rischi e garantiamo prestazioni sostenibili nel tempo.",
        "Il nostro approccio olistico permette di anticipare, adattarsi e resistere alle sfide future, mantenendo l'innovazione al centro di ogni progetto. Costruiamo le fondamenta di un futuro più sicuro, efficiente e resiliente.",
      ],
    },
    mission: {
      overline: "La Missione",
      title: "Trasformare l'incertezza in vantaggio.",
      body:
        "Il mondo è cambiato. Ci impegniamo a rispondere alle nuove sfide con tecniche avanzate, per ottenere maggiore efficienza, produttività e sostenibilità economica ed ambientale. Data la natura incerta dei nostri giorni e la dipendenza da contesti esterni non controllabili, trasformiamo i sistemi esistenti in processi resilienti.",
      pillars: [
        {
          title: "Efficienza",
          desc: "Ottimizziamo ogni processo riducendo costi, sprechi e tempi senza compromessi sulla qualità.",
        },
        {
          title: "Sostenibilità",
          desc: "Soluzioni che migliorano l'impatto ambientale e creano valore economico duraturo.",
        },
        {
          title: "Resilienza",
          desc: "Sistemi robusti capaci di assorbire gli shock e adattarsi a scenari imprevedibili.",
        },
      ],
    },
    method: {
      overline: "Cosa ci Rende Speciali",
      title: "Una metodologia precisa per dominare la complessità.",
      body:
        "Utilizziamo un metodo rigoroso per affrontare le sfide che rendono i sistemi complessi difficili da governare. I nostri strumenti spaziano dalla simulazione ai metodi statistici, dal machine learning ai Large Language Models (LLM).",
      tools: [
        { name: "Simulazione", desc: "Modelli predittivi e digital twin per testare scenari prima della realtà." },
        { name: "Metodi Statistici", desc: "Analisi dei dati rigorosa per decisioni basate sull'evidenza." },
        { name: "Machine Learning", desc: "Algoritmi che apprendono dai dati per anticipare comportamenti." },
        { name: "Large Language Models", desc: "Intelligenza artificiale generativa applicata a processi e conoscenza." },
      ],
    },
    services: {
      overline: "I Servizi",
      title: "Competenze integrate, dal rilievo alla strategia.",
      items: [
        { name: "Rilievi ed Ispezioni", desc: "Acquisizione precisa dello stato di fatto di sistemi e infrastrutture." },
        { name: "Modellazione dei Sistemi", desc: "Rappresentazione formale di processi complessi per analisi e ottimizzazione." },
        { name: "Valutazione di Rischi e Opportunità", desc: "Identificazione strutturata di minacce e leve di crescita." },
        { name: "Pianificazione e Controllo di Progetto", desc: "Scheduling, budget e monitoraggio per progetti sotto controllo." },
        { name: "Analisi dei Rischi di Progetto e Prodotto", desc: "Risk assessment quantitativo lungo tutto il ciclo di vita." },
        { name: "Project Management", desc: "Gestione end-to-end di progetti complessi e multidisciplinari." },
        { name: "Formazione", desc: "Percorsi formativi su sistemi, rischio e tecnologie avanzate." },
        { name: "Sistemi di Controllo & IoT", desc: "Progettazione di reti di sensori, automazione e monitoraggio." },
        { name: "Consulenza su Sistemi Complessi", desc: "Advisory su ingegneria, economia, politica, start-up e imprese." },
        { name: "Finanza Agevolata", desc: "Accesso a bandi, incentivi e strumenti di finanziamento agevolato." },
      ],
    },
    why: {
      overline: "Perché Sceglierci",
      title: "Esperienza e sangue freddo.",
      body:
        "Più di 25 anni di esperienza nella gestione di progetti complessi, in alcuni dei settori più esigenti e regolamentati al mondo. Una visione trasversale che unisce rigore ingegneristico e pragmatismo operativo.",
      sectors: ["ICT", "Oil & Gas", "Government PAC & PAL", "Insurance", "Engineering", "Manufacturing", "Research & Development"],
    },
    contact: {
      overline: "Contatti",
      title: "Costruiamo insieme un futuro resiliente.",
      body: "Raccontaci la tua sfida. Rispondiamo con metodo, tecnologia e visione.",
      form: {
        name: "Nome e Cognome",
        email: "Email",
        message: "Il tuo messaggio",
        submit: "Invia richiesta",
        sending: "Invio in corso…",
        success: "Messaggio inviato! Ti risponderemo al più presto.",
        error: "Invio non riuscito. Riprova o scrivici via email.",
      },
      labels: { address: "Sede", email: "Email", phone: "Telefono" },
    },
    footer: {
      tagline: "Ingegneria dei Sistemi e della Resilienza",
      rights: "Tutti i diritti riservati.",
    },
  },
  en: {
    nav: {
      about: "Studio",
      mission: "Mission",
      method: "Method",
      services: "Services",
      why: "Why Us",
      contact: "Contact",
      cta: "Let's talk",
    },
    hero: {
      overline: "Systems & Resilience Engineering",
      title: "Govern Chaos with Method.",
      subtitle:
        "We design robust, resilient systems for a complex world. Advanced technologies, engineering rigor and strategic vision to anticipate, adapt and withstand the challenges ahead.",
      ctaPrimary: "Start a project",
      ctaSecondary: "Explore the studio",
      stats: [
        { value: "25+", label: "Years of experience" },
        { value: "6", label: "Industrial sectors" },
        { value: "∞", label: "Resilient systems" },
      ],
    },
    about: {
      overline: "The Studio",
      title: "Better with Less.",
      paragraphs: [
        "We explore systems engineering to design robust, resilient solutions in an increasingly complex and interconnected world. Through the integration of advanced technologies, we improve process reliability, reduce risk and ensure sustainable performance over time.",
        "Our holistic approach makes it possible to anticipate, adapt and withstand future challenges, keeping innovation at the core of every project. We build the foundations of a safer, more efficient and resilient future.",
      ],
    },
    mission: {
      overline: "The Mission",
      title: "Turning uncertainty into advantage.",
      body:
        "The world has changed. We are committed to answering new challenges with advanced techniques, to achieve greater efficiency, productivity and economic and environmental sustainability. Given the uncertain nature of our times and the dependence on uncontrollable external contexts, we transform existing systems into resilient processes.",
      pillars: [
        { title: "Efficiency", desc: "We optimize every process, cutting cost, waste and time with no compromise on quality." },
        { title: "Sustainability", desc: "Solutions that improve environmental impact and create lasting economic value." },
        { title: "Resilience", desc: "Robust systems able to absorb shocks and adapt to unpredictable scenarios." },
      ],
    },
    method: {
      overline: "What Makes Us Special",
      title: "A precise methodology to master complexity.",
      body:
        "We apply a rigorous method to tackle the challenges that make complex systems hard to govern. Our tools range from simulation to statistical methods, from machine learning to Large Language Models (LLMs).",
      tools: [
        { name: "Simulation", desc: "Predictive models and digital twins to test scenarios before reality." },
        { name: "Statistical Methods", desc: "Rigorous data analysis for evidence-based decisions." },
        { name: "Machine Learning", desc: "Algorithms that learn from data to anticipate behavior." },
        { name: "Large Language Models", desc: "Generative AI applied to processes and knowledge." },
      ],
    },
    services: {
      overline: "Services",
      title: "Integrated expertise, from survey to strategy.",
      items: [
        { name: "Surveys & Inspections", desc: "Precise acquisition of the as-is state of systems and infrastructure." },
        { name: "Systems Modeling", desc: "Formal representation of complex processes for analysis and optimization." },
        { name: "Risk & Opportunity Assessment", desc: "Structured identification of threats and growth levers." },
        { name: "Project Planning & Control", desc: "Scheduling, budgeting and monitoring to keep projects on track." },
        { name: "Project & Product Risk Analysis", desc: "Quantitative risk assessment across the full life cycle." },
        { name: "Project Management", desc: "End-to-end management of complex, multidisciplinary projects." },
        { name: "Training", desc: "Learning paths on systems, risk and advanced technologies." },
        { name: "Control Systems & IoT", desc: "Design of sensor networks, automation and monitoring." },
        { name: "Complex Systems Consulting", desc: "Advisory across engineering, economics, policy, start-ups and enterprise." },
        { name: "Facilitated Finance", desc: "Access to grants, incentives and subsidized funding instruments." },
      ],
    },
    why: {
      overline: "Why Choose Us",
      title: "Experience and a cool head.",
      body:
        "More than 25 years of experience managing complex projects, across some of the most demanding and regulated industries in the world. A cross-domain vision that blends engineering rigor with operational pragmatism.",
      sectors: ["ICT", "Oil & Gas", "Government PAC & PAL", "Insurance", "Engineering", "Manufacturing", "Research & Development"],
    },
    contact: {
      overline: "Contact",
      title: "Let's build a resilient future together.",
      body: "Tell us about your challenge. We answer with method, technology and vision.",
      form: {
        name: "Full name",
        email: "Email",
        message: "Your message",
        submit: "Send request",
        sending: "Sending…",
        success: "Message sent! We'll get back to you soon.",
        error: "Something went wrong. Please try again or email us.",
      },
      labels: { address: "Office", email: "Email", phone: "Phone" },
    },
    footer: {
      tagline: "Systems & Resilience Engineering",
      rights: "All rights reserved.",
    },
  },
};

export const contactInfo = {
  address: "Via Salita Larderia – Zona ASI – c/o co-working SmartMe, 98129 Messina, Italy",
  email: "az@angelozaia.com",
};
